import { Bookmark } from 'lucide-react';
import { motion } from 'framer-motion';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useLanguage } from '@/i18n/LanguageContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface BookmarkButtonProps {
  roomId: string;
  className?: string;
  size?: 'sm' | 'md';
}

export const BookmarkButton = ({ roomId, className = '', size = 'md' }: BookmarkButtonProps) => {
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const { t } = useLanguage();
  const { tap } = useHapticFeedback();
  
  const bookmarked = isBookmarked(roomId);
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark(roomId);
    tap(bookmarked ? 'light' : 'medium');
  };

  return (
    <motion.button
      onClick={handleClick}
      className={`p-1.5 rounded transition-all duration-200 ${
        bookmarked 
          ? 'text-primary bg-primary/10' 
          : 'text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/20'
      } ${className}`}
      whileTap={{ scale: 0.9 }}
      title={bookmarked ? (t('remove_bookmark') || 'Remove bookmark') : (t('add_bookmark') || 'Add bookmark')}
      aria-label={bookmarked ? 'Remove bookmark' : 'Add bookmark'}
    >
      <Bookmark 
        className={iconSize} 
        fill={bookmarked ? 'currentColor' : 'none'}
      />
    </motion.button>
  );
};

export default BookmarkButton;
